/** What the user tried to delete, for the wording of a refused delete. */
export type DeleteTarget = 'order' | 'project' | 'contract' | 'employmentSchedule';

interface Blocker {
  one: string;
  many: string;
  /** What to remove first, when the user can do that; history such as time reports cannot go. */
  removeFirst?: { one: string; many: string };
  /** Leave out the count, because it includes rows the app no longer shows. */
  uncounted?: boolean;
}

// The backend's metadata.reason when a delete is refused because other rows refer to the item.
// It comes with the number of those rows as count.
const blockers: Record<string, Blocker> = {
  hasQualityControls: {
    one: 'kvalitetskontroll',
    many: 'kvalitetskontroller',
    removeFirst: { one: 'kontrollen', many: 'kontrollerna' },
  },
  hasOrders: { one: 'order', many: 'ordrar', removeFirst: { one: 'ordern', many: 'ordrarna' } },
  hasCases: { one: 'ärende', many: 'ärenden', removeFirst: { one: 'ärendet', many: 'ärendena' } },
  hasTimeReports: { one: 'tidrapport', many: 'tidrapporter' },
  // Scheduled tasks are only flagged as deleted, so the count includes tasks nobody sees.
  hasScheduledTasks: { one: 'planerat uppdrag', many: 'planerade uppdrag', uncounted: true },
  hasReportedMissingProducts: {
    one: 'rapporterad saknad produkt',
    many: 'rapporterade saknade produkter',
  },
  // The backend counts employment periods, past ones included, not people.
  hasEmployees: {
    one: 'medarbetare, nu eller tidigare,',
    many: 'medarbetare, nu eller tidigare,',
    uncounted: true,
  },
};

const subjects: Record<Exclude<DeleteTarget, 'contract'>, string> = {
  order: 'Ordern',
  project: 'Projektet',
  employmentSchedule: 'Anställningsschemat',
};

/**
 * The Swedish message for a 409 that refuses a delete, or undefined when the reason is not one.
 * Without a target the wording names neither order, project nor contract.
 */
export const deleteRefusedMessage = (
  reason: string | undefined,
  count: number | undefined,
  target?: DeleteTarget
): string | undefined => {
  const blocker = blockers[reason ?? ''];
  if (!blocker) {
    return undefined;
  }

  const things =
    count === undefined || blocker.uncounted
      ? blocker.many
      : `${count} ${count === 1 ? blocker.one : blocker.many}`;
  const removeFirst = blocker.removeFirst
    ? ` Ta bort ${count === 1 ? blocker.removeFirst.one : blocker.removeFirst.many} först.`
    : '';

  // Deleting a contract deletes its orders, so what blocks it sits on the orders.
  if (target === 'contract') {
    return `Offerten kan inte tas bort eftersom dess ordrar har ${things}.${removeFirst}`;
  }
  if (target) {
    return `${subjects[target]} har ${things} och kan inte tas bort.${removeFirst}`;
  }
  return `Det finns ${things} som hindrar borttagningen.${removeFirst}`;
};
